// Persists the DPoP key pair in IndexedDB so it survives a full page reload (F5, the
// Stripe Checkout redirect, SenSai connect) (EEDTD-101).
//
// Why IndexedDB: a CryptoKey can be structured-cloned into IndexedDB and stays
// non-extractable there. Page JavaScript can load it and sign with it, but can never
// read or export the private key material, which is the same guarantee as holding it
// in Redux memory. localStorage cannot hold a CryptoKey at all.
//
// Why it matters: the backend binds a session to the key's fingerprint. Without this
// store the key was lost on every reload and the FE had to present a new one, which a
// bound SSO session (correctly) rejects with 644, logging the user out.
//
// Every function fails soft: IndexedDB can be missing or throw (some private-browsing
// modes, storage disabled). Callers treat "no stored key" as the fallback path, so an
// unavailable store degrades to the old behaviour instead of breaking the app.
//
// No connection is kept open between calls. An open connection would block
// indexedDB.deleteDatabase() on logout until the page closed.

// Dedicated database, so logout can delete it by name (see clearBrowserStorage.js).
export const DPOP_KEY_DB_NAME = 'simpli5-dpop'
// Bump only when the object-store layout changes.
const DPOP_KEY_DB_VERSION = 1
// Single object store holding the current session's key pair.
const DPOP_KEY_STORE = 'keys'
// Only one key pair is ever stored: the one for the current session. A new login
// overwrites it under the same record key.
const CURRENT_KEY = 'current'

const isIndexedDBAvailable = () => {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null
  } catch (error) {
    // Some sandboxed contexts throw on property access itself.
    return false
  }
}

// Opens the database, creating the object store on first use. Resolves the connection;
// rejects on error or when the open is blocked by another tab mid-upgrade.
const openKeyDatabase = () =>
  new Promise((resolve, reject) => {
    // A blocked open is not cancelled by rejecting: the request stays pending and can
    // still fire onsuccess once the other connection closes. By then nobody is waiting
    // for the connection, so it must be closed here, or it would be left open and block
    // deleteDatabase() on logout.
    let settled = false
    const request = indexedDB.open(DPOP_KEY_DB_NAME, DPOP_KEY_DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(DPOP_KEY_STORE)) {
        db.createObjectStore(DPOP_KEY_STORE)
      }
    }
    request.onsuccess = () => {
      const db = request.result
      if (settled) {
        db.close()
        return
      }
      settled = true
      // Another tab is deleting or upgrading the database (logout): let it proceed.
      db.onversionchange = () => db.close()
      resolve(db)
    }
    request.onerror = () => {
      settled = true
      reject(request.error)
    }
    request.onblocked = () => {
      settled = true
      reject(new Error('DPoP key database open blocked'))
    }
  })

// Runs one request against the object store in its own transaction, then closes the
// connection. Resolves with the request's result once the transaction has committed,
// so a save is durable before the promise settles.
const runInStore = (mode, buildRequest) =>
  openKeyDatabase().then(
    db =>
      new Promise((resolve, reject) => {
        let result
        let transaction
        try {
          transaction = db.transaction(DPOP_KEY_STORE, mode)
          const request = buildRequest(transaction.objectStore(DPOP_KEY_STORE))
          request.onsuccess = () => {
            result = request.result
          }
        } catch (error) {
          db.close()
          reject(error)
          return
        }
        transaction.oncomplete = () => {
          db.close()
          resolve(result)
        }
        transaction.onerror = () => {
          db.close()
          reject(transaction.error)
        }
        transaction.onabort = () => {
          db.close()
          reject(transaction.error)
        }
      }),
  )

// Stores the session's key pair, replacing any previous one.
// keyPair: CryptoKeyPair from generateDpopKeyPair(). Stored as a plain object of its two
//           CryptoKeys, which is what structured clone and createDpopProof both expect.
// Resolves true when stored, false otherwise. Never rejects.
export const saveDpopKeyPair = async keyPair => {
  if (!keyPair || !keyPair.privateKey || !keyPair.publicKey || !isIndexedDBAvailable()) {
    return false
  }
  try {
    await runInStore('readwrite', store =>
      store.put({ privateKey: keyPair.privateKey, publicKey: keyPair.publicKey }, CURRENT_KEY),
    )
    return true
  } catch (error) {
    return false
  }
}

// Loads the stored key pair.
// Resolves { privateKey, publicKey } when a complete pair is stored, otherwise null
// (nothing stored, a malformed record, or IndexedDB unavailable). Never rejects.
export const loadDpopKeyPair = async () => {
  if (!isIndexedDBAvailable()) {
    return null
  }
  try {
    const record = await runInStore('readonly', store => store.get(CURRENT_KEY))
    if (record && record.privateKey && record.publicKey) {
      return { privateKey: record.privateKey, publicKey: record.publicKey }
    }
    return null
  } catch (error) {
    return null
  }
}

// Deletes the whole key database. Called on logout and session expiry by name, because
// the generic IndexedDB cleanup relies on indexedDB.databases(), which some Safari
// versions do not implement. Resolves once the deletion completes, fails, or is queued
// behind another tab's connection (see onblocked below). Never rejects.
export const deleteDpopKeyPair = () =>
  new Promise(resolve => {
    if (!isIndexedDBAvailable()) {
      resolve()
      return
    }
    try {
      const request = indexedDB.deleteDatabase(DPOP_KEY_DB_NAME)
      request.onsuccess = () => resolve()
      request.onerror = () => resolve()
      // Blocked means another tab still holds a connection (onversionchange above makes it
      // close). The deletion stays queued, and IndexedDB processes open/delete requests for
      // the same database in order, so a later saveDpopKeyPair() cannot run before it: the
      // next session's key always lands in the recreated database. Resolving here instead of
      // waiting for onsuccess keeps logout and SSO login from hanging on a tab that never
      // releases its connection (e.g. a frozen background tab).
      request.onblocked = () => resolve()
    } catch (error) {
      resolve()
    }
  })
